import React, { useEffect, useState } from "react";
import IsLogin from "../../../Common/IsLogin";
import Http from "../../../Common/Http";

const Zkteco = () => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [connect, setConnect] = useState();
    const [users, setUsers] = useState([]);
    const [showUserForm, setShowUserForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        uid: '',
        userid: '',
        name: '',
        password: '',
        role: 0, // 0 for user, 14 for admin
        cardno: ''
    });
    const [formErrors, setFormErrors] = useState({});

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await Http.get("/zkteco/connect");
            setConnect(res.data);
            setMessage(res.data.message);
            
            // If connected, also fetch users
            if (res.data.status === true) {
                await fetchUsers();
            }
        } catch (err) {
            setMessage(err.response?.data?.message || "Connection failed");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const res = await Http.post("/zkteco/users");
            if (res.data.status) {
                setUsers(res.data.data || []);
            }
        } catch (err) {
            console.error("Failed to fetch users:", err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSyncUsers = async () => {
        setLoading(true);
        try {
            const res = await Http.get("/zkteco/sync-users");
            setMessage("Users synced successfully");
            await fetchUsers(); // Refresh user list
        } catch (err) {
            setMessage(err.response?.data?.message || "Sync failed");
        } finally {
            setLoading(false);
        }
    };

    const handleSyncUsersAttendance = async () => {
        setLoading(true);
        try {
            const res = await Http.get("/zkteco/sync-users-attendance");
            setMessage("Attendance synced successfully");
        } catch (err) {
            setMessage(err.response?.data?.message || "Sync failed");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.uid || formData.uid < 1 || formData.uid > 65535) {
            errors.uid = 'UID is required and must be between 1 and 65535';
        }
        
        if (!formData.userid || formData.userid.length > 9) {
            errors.userid = 'User ID is required and must not exceed 9 characters';
        }
        
        if (!formData.name || formData.name.length > 24) {
            errors.name = 'Name is required and must not exceed 24 characters';
        }
        
        if (formData.password && formData.password.length > 8) {
            errors.password = 'Password must not exceed 8 characters';
        }
        
        if (formData.cardno && (formData.cardno.length > 10 || !/^\d+$/.test(formData.cardno))) {
            errors.cardno = 'Card number must be numeric and not exceed 10 digits';
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setLoading(true);
        try {
            const submitData = {
                ...formData,
                uid: parseInt(formData.uid),
                role: parseInt(formData.role),
                cardno: formData.cardno ? parseInt(formData.cardno) : 0
            };

            let res;
            if (editingUser) {
                res = await Http.put(`/zkteco/users/${editingUser.uid}`, submitData);
            } else {
                res = await Http.post("/zkteco/users", submitData);
            }

            if (res.data.status) {
                setMessage(`User ${editingUser ? 'updated' : 'created'} successfully`);
                setShowUserForm(false);
                setEditingUser(null);
                resetForm();
                await fetchUsers(); // Refresh user list
            } else {
                setMessage(res.data.message);
            }
        } catch (err) {
            if (err.response?.data?.errors) {
                setFormErrors(err.response.data.errors);
            }
            setMessage(err.response?.data?.message || `Failed to ${editingUser ? 'update' : 'create'} user`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            uid: user.uid,
            userid: user.userid,
            name: user.name,
            password: '', // Don't show existing password
            role: user.role || 0,
            cardno: user.cardno || ''
        });
        setShowUserForm(true);
        setFormErrors({});
    };

    // const handleDelete = async (uid) => {
    //     if (!window.confirm('Are you sure you want to delete this user?')) {
    //         return;
    //     }

    //     setLoading(true);
    //     try {
    //         const res = await Http.delete(`/zkteco/users/${uid}`);
    //         if (res.data.status) {
    //             setMessage('User deleted successfully');
    //             await fetchUsers(); // Refresh user list
    //         } else {
    //             setMessage(res.data.message);
    //         }
    //     } catch (err) {
    //         setMessage(err.response?.data?.message || 'Failed to delete user');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const resetForm = () => {
        setFormData({
            uid: '',
            userid: '',
            name: '',
            password: '',
            role: 0,
            cardno: ''
        });
        setFormErrors({});
    };

    const handleCancelForm = () => {
        setShowUserForm(false);
        setEditingUser(null);
        resetForm();
    };

    return (
        <div>
            <IsLogin />
            <div className="am-mainpanel">
                <div className="am-pagetitle">
                    <h5 className="am-title">ZKTeco Device Management</h5>
                </div>
                <div className="am-pagebody">
                    {/* Device Connection Card */}
                    <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">Connect to ZKTeco Device</h6>
                            <button 
                                onClick={fetchData} 
                                className="btn btn-outline-secondary btn-sm"
                                disabled={loading}
                            >
                                {loading ? 'Refreshing...' : 'Refresh Connection'}
                            </button>
                        </div>
                        <div className="card-body">
                            {/* Status message */}
                            {message && (
                                <div className={`alert ${connect?.status === true ? 'alert-success' : 'alert-danger'} mt-3`}>
                                    {message}
                                </div>
                            )}
                            
                            {/* Device Status */}
                            <h6 className="mb-3">
                                Device Status: 
                                <span className={`ms-2 badge ${connect?.status === true ? 'bg-success' : 'bg-danger'}`}>
                                    {connect?.status === true ? "Connected" : "Disconnected"}
                                </span>
                            </h6>
                            
                            {connect?.status === true && (
                                <div className="d-flex gap-2 flex-wrap">
                                    <button 
                                        onClick={handleSyncUsers} 
                                        className="btn btn-primary btn-sm"
                                        disabled={loading}
                                    >
                                        {loading ? 'Syncing...' : 'Sync Users'}
                                    </button>
                                    <button 
                                        onClick={handleSyncUsersAttendance} 
                                        className="btn btn-info btn-sm"
                                        disabled={loading}
                                    >
                                        {loading ? 'Syncing...' : 'Sync Attendance'}
                                    </button>
                                    {/* <button 
                                        onClick={() => {
                                            setShowUserForm(true);
                                            setEditingUser(null);
                                            resetForm();
                                        }} 
                                        className="btn btn-success btn-sm"
                                    >
                                        Add New User
                                    </button> */}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* User Form Modal/Card */}
                    {showUserForm && (
                        <div className="card mb-4">
                            <div className="card-header">
                                <h6 className="card-title mb-0">
                                    {editingUser ? 'Edit User' : 'Add New User'}
                                </h6>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">UID *</label>
                                            <input
                                                type="number"
                                                className={`form-control ${formErrors.uid ? 'is-invalid' : ''}`}
                                                name="uid"
                                                value={formData.uid}
                                                onChange={handleInputChange}
                                                min="1"
                                                max="65535"
                                                disabled={editingUser} // UID cannot be changed when editing
                                                required
                                            />
                                            {formErrors.uid && <div className="invalid-feedback">{formErrors.uid}</div>}
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">User ID *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.userid ? 'is-invalid' : ''}`}
                                                name="userid"
                                                value={formData.userid}
                                                onChange={handleInputChange}
                                                maxLength="9"
                                                required
                                            />
                                            {formErrors.userid && <div className="invalid-feedback">{formErrors.userid}</div>}
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Name *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${formErrors.name ? 'is-invalid' : ''}`}
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                maxLength="24"
                                                required
                                            />
                                            {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Password</label>
                                            <input
                                                type="password"
                                                className={`form-control ${formErrors.password ? 'is-invalid' : ''}`}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                maxLength="8"
                                            />
                                            {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Role</label>
                                            <select
                                                className="form-control"
                                                name="role"
                                                value={formData.role}
                                                onChange={handleInputChange}
                                            >
                                                <option value={0}>User</option>
                                                <option value={14}>Admin</option>
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Card Number</label>
                                            <input
                                                type="number"
                                                className={`form-control ${formErrors.cardno ? 'is-invalid' : ''}`}
                                                name="cardno"
                                                value={formData.cardno}
                                                onChange={handleInputChange}
                                            />
                                            {formErrors.cardno && <div className="invalid-feedback">{formErrors.cardno}</div>}
                                        </div>
                                    </div>
                                    
                                    <div className="d-flex gap-2">
                                        <button 
                                            type="submit" 
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                                        </button>
                                        <button 
                                            type="button" 
                                            className="btn btn-secondary"
                                            onClick={handleCancelForm}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Users List */}
                    {/* {connect?.status === true && (
                        <div className="card">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h6 className="card-title mb-0">Device Users ({users.length})</h6>
                                <button 
                                    onClick={fetchUsers} 
                                    className="btn btn-outline-primary btn-sm"
                                    disabled={loading}
                                >
                                    Refresh List
                                </button>
                            </div>
                            <div className="card-body">
                                {users.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>UID</th>
                                                    <th>User ID</th>
                                                    <th>Name</th>
                                                    <th>Role</th>
                                                    <th>Card No</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {users.map((user, index) => (
                                                    <tr key={index}>
                                                        <td>{user.uid}</td>
                                                        <td>{user.userid}</td>
                                                        <td>{user.name}</td>
                                                        <td>
                                                            <span className={`badge ${user.role === 14 ? 'bg-warning' : 'bg-info'}`}>
                                                                {user.role === 14 ? 'Admin' : 'User'}
                                                            </span>
                                                        </td>
                                                        <td>{user.cardno || 'N/A'}</td>
                                                        <td>
                                                            <div className="d-flex gap-1">
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => handleEdit(user)}
                                                                    disabled={loading}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDelete(user.uid)}
                                                                    disabled={loading}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No users found on the device</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )} */}
                </div>
            </div>
        </div>
    );
};

export default Zkteco;